import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Typography,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { INPUT_MAX_LENGTH } from "@/constants/Fields";
import { templateCategories } from "@/constants/TemplateCategories";
import {
  ASSET_TYPES_FIELDS,
  ASSET_TYPES_FILTERS,
  useGetAssetTypes,
} from "@/entities/asset-types";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import { AssetTypesSelect } from "@/modules/asset-types/asset-types-select";
import { validationErrorParser } from "@/modules/form-validation";
import { getSelectMultipleValues } from "@/modules/select";
import { useSidePanel } from "@/modules/side-panel";

import { TreeModifier } from "./TreeModifier";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm } = Form;
const { Text } = Typography;

const SectionEdit = ({ template, layoutIndex }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { closeSidePanel } = useSidePanel();
  const { onError } = useApiErrorHandler();

  const mutation = useUpdate("interventionTemplates", template.id);

  const categories = templateCategories.map((category) => ({
    label: t(`interventionTemplate:category.${category}`),
    value: category,
  }));

  const section = TreeModifier.find(
    template,
    "templates",
    (template) => template.layout_index == layoutIndex
  );

  const [isLoopOnChildren, setIsLoopOnChildren] = useState(
    section.flags?.loopOnChildren ?? false
  );
  const [isAllTypes, setIsAllTypes] = useState(!section.types?.length);

  const { isLoading, assetTypes } = useGetAssetTypes({
    params: {
      [`page[size]`]: 50,
      [ASSET_TYPES_FIELDS.ASSET_TYPES]: "id,name",
      [ASSET_TYPES_FILTERS.ID]: section.types?.join(","),
    },
    enabled: !isAllTypes,
  });

  const initialAssetTypes = assetTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const defaultValues = {
    name: section.name,
    description: section.description,
    allTypes: isAllTypes,
    dynamicFlag: section.flags?.dynamic ?? false,
    loopOnChildrenFlag: isLoopOnChildren ? 1 : 2,
    category: section.category,
  };

  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(
          mutation.error,
          ["name", "description", "category"],
          "others"
        )
      : {};
  }

  /**
   * Closes the current edit panel.
   *
   * @return {void}
   */
  function onClose() {
    form.resetFields();
    closeSidePanel();
  }

  function validation(item) {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  }

  /**
   * Attempts to update asset
   *
   * @return {void}
   */
  // TODO IMP-1217 fix require-await
  // eslint-disable-next-line require-await
  async function onUpdate() {
    const loopOnChildrenFlag = form.getFieldValue("loopOnChildrenFlag");
    const isLoopOnChildrenSelected = loopOnChildrenFlag === 1;
    const isAllTypesSelected = form.getFieldValue("allTypes");
    const assetTypes = getSelectMultipleValues(form, "assetTypes");

    const updatedTemplate = TreeModifier.findAndReplace(
      template,
      "templates",
      (section) =>
        (!layoutIndex && !section.layout_index) ||
        section.layout_index == layoutIndex,
      {
        layout_index: section.layout_index,
        name: form.getFieldValue("name"),
        description: form.getFieldValue("description"),
        templates: section.templates,
        type: section.type,
        types:
          !isLoopOnChildrenSelected || isAllTypesSelected ? [] : assetTypes,
        flags: {
          dynamic: form.getFieldValue("dynamicFlag"),
          loopOnChildren: isLoopOnChildrenSelected,
        },
        category: form.getFieldValue("category"),
      }
    );

    mutation.mutate(
      { templates: updatedTemplate.templates },
      {
        onSuccess: function () {
          onClose();
        },
        onError,
      }
    );
  }

  function onloopOnChildrenSelect() {
    const isLoopOnChildrenSelected =
      form.getFieldValue("loopOnChildrenFlag") == 1 ? true : false;
    setIsLoopOnChildren(isLoopOnChildrenSelected);
  }

  function onAllTypesCheck() {
    setIsAllTypes(form.getFieldValue("allTypes"));
  }

  return (
    <Form
      form={form}
      initialValues={defaultValues}
      layout="vertical"
      validateMessages={t("asset:form.validateMessages")}
      onFinish={onUpdate}
    >
      <Form.Item
        name="name"
        label={t("asset:form.name.label")}
        {...validation("name")}
        rules={[{ required: true }]}
      >
        <Input
          data-testid="name"
          placeholder={t("asset:form.name.label")}
          maxLength={INPUT_MAX_LENGTH}
          showCount
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("asset:form.description.label")}
        {...validation("description")}
      >
        <Input
          data-testid="description"
          placeholder={t("asset:form.description.label")}
        />
      </Form.Item>
      <Form.Item
        name="category"
        label={t("interventionTemplate:form.category.label")}
        rules={[{ required: true }]}
        {...validation("category")}
      >
        <Select placeholder={t("interventionTemplate:form.category.label")}>
          {categories.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Space orientation="vertical" size="middle">
        <Text strong>{t("interventionTemplate:form.flags.title")}</Text>
        <Form.Item noStyle name="dynamicFlag" valuePropName="checked">
          <Checkbox data-testid="dynamicFlag">
            {t("interventionTemplate:form.flags.dynamic")}
          </Checkbox>
        </Form.Item>
        <Form.Item name="loopOnChildrenFlag">
          <Radio.Group onChange={onloopOnChildrenSelect}>
            <Space orientation="vertical">
              <Radio value={1}>
                {t("interventionTemplate:form.flags.loopOnChildren")}
              </Radio>
              <Radio value={2}>
                {t("interventionTemplate:form.flags.displayForCurrentAsset")}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Text strong>
          {t("interventionTemplate:form.assetTypeFilter.label")}
        </Text>
        <Form.Item noStyle name="allTypes" valuePropName="checked">
          <Checkbox disabled={!isLoopOnChildren} onChange={onAllTypesCheck}>
            {t("interventionTemplate:form.assetTypes.allTypes.label")}
          </Checkbox>
        </Form.Item>
        <AssetTypesSelect
          disabled={!isLoopOnChildren || isAllTypes}
          name="assetTypes"
          parentLoading={isLoading}
          initialValue={!isAllTypes ? initialAssetTypes : []}
          pageSize={50}
          {...validation("asset_type_id")}
        />
      </Space>
      {validationErrors().layout_index && (
        <Form.Item {...validation("layout_index")} />
      )}
      <Divider />
      <Form.Item>
        <Flex gap="small">
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            name="update"
            data-testid="update"
            htmlType="submit"
          >
            {t("global:update")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );
};

export default SectionEdit;
